import React, { useEffect, useRef, useState } from 'react';
import { BsCheck } from 'react-icons/bs';
import { FieldValues, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { BiPlus } from 'react-icons/bi';
import { IoTrash } from 'react-icons/io5';
import styles from './phase-form.module.scss';

import {
  InputNumber,
  InputText,
  Checkbox,
} from '~/lib/HooksFormFields';

import { IWorksite, WorksiteFitter, WorksitePhase } from '~/types/worksites';
import { useUpdateWorksite } from '~/hooks/worksites';
import Modal from '~/lib/Modal';
import FitterModal from '../FitterModal';

const Fitter = ({
  fitter,
  openFitter,
  disabled = false,
}:{
  fitter: WorksiteFitter,
  openFitter: () => void,
  disabled?: boolean
}) => (
    <div className={`${styles.fitter} ${disabled ? styles.disabled : ''}`}
      onClick={() => !disabled && openFitter()}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div className={styles.title}>
        <h4>{fitter.user.profile.firstName} {fitter.user.profile.lastName}</h4>
      </div>
      <p>
        {fitter.startDate && `Date de début : ${format(new Date(fitter.startDate), 'dd.MM.yyyy')}`}
        {fitter.endDate && ` - Date de fin : ${format(new Date(fitter.endDate), 'dd.MM.yyyy')}`}
      </p>
    </div>
);

const WorksitePhaseForm = ({
  worksite,
  phase,
  close,
} : {
  worksite?: IWorksite | null,
  phase: WorksitePhase,
  close: () => void,
}) => {
  const fitterModalRef = useRef<any>();
  const [selectedFitter, setSelectedFitter] = useState<WorksiteFitter | null>(null);
  const {
    isLoading,
    mutateAsync: updateWorksite,
  } = useUpdateWorksite();

  const {
    control, handleSubmit, reset, watch,
  } = useForm<FieldValues>({
    defaultValues: {
      ...phase,
      startDate: phase?.startDate ? format(new Date(phase.startDate), 'yyyy-MM-dd') : null,
      endDate: phase?.endDate ? format(new Date(phase.endDate), 'yyyy-MM-dd') : null,
      fitters: phase.fitters.map(
        (f) => ({ ...f, user: f.user?._id }),
      ),
    },
  });

  // Surveiller les changements dans les champs critiques
  const watchedFields = watch(['name', 'startDate', 'endDate', 'hours']);

  // Vérifier si les données ont été modifiées par rapport aux données sauvegardées
  const hasUnsavedChanges = () => {
    const [name, startDate, endDate, hours] = watchedFields;

    // Comparer avec les données originales de la phase
    const originalStartDate = phase?.startDate ? format(new Date(phase.startDate), 'yyyy-MM-dd') : null;
    const originalEndDate = phase?.endDate ? format(new Date(phase.endDate), 'yyyy-MM-dd') : null;

    return (
      name !== phase?.name
      || startDate !== originalStartDate
      || endDate !== originalEndDate
      || hours !== phase?.hours
    );
  };

  const submit = async (data: any) => {
    try {
      // Traiter les données du formulaire avant de les envoyer
      const processedData = {
        ...data,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : null,
        endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
        // Les fitters sont déjà au bon format dans le formulaire
      };

      const phases = worksite?.phases ? [...worksite.phases.map(
        (p) => ({
          ...p,
          fitters: p.fitters.map(
            (f) => ({ ...f, user: f.user?._id }),
          ),
        }),
      )] : [];
      const index = phases.findIndex((p) => p._id === processedData._id);
      if (index !== -1) {
        phases[index] = processedData;
      }
      const payload : any = {
        _id: worksite?._id,
        phases,
      };
      const { worksite: worksiteUpdated } = await updateWorksite(payload);
      if (worksiteUpdated) {
        close();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const deletePhase = async () => {
    try {
      const phases = worksite?.phases ? [...worksite.phases.map(
        (p) => ({
          ...p,
          fitters: p.fitters.map(
            (f) => ({ ...f, user: f.user?._id }),
          ),
        }),
      )] : [];
      const payload : any = {
        _id: worksite?._id,
        phases: phases.filter((p:any) => p._id !== phase?._id),
      };
      const { worksite: worksiteUpdated } = await updateWorksite(payload);
      if (worksiteUpdated) {
        close();
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (phase) {
      const resetPhase = {
        ...phase,
        startDate: phase?.startDate ? format(new Date(phase.startDate), 'yyyy-MM-dd') : null,
        endDate: phase?.endDate ? format(new Date(phase.endDate), 'yyyy-MM-dd') : null,
        fitters: phase.fitters.map(
          (f) => ({ ...f, user: f.user?._id }),
        ),
      };
      reset(resetPhase);
    }
  }, [phase]);

  return (
    <>
      <div className={styles.form}>
        <div className={`${styles.containerField} ${styles.name}`}>
          <InputText
            name="name"
            control={control}
          />
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              type="date"
              label="Date de début"
              name="startDate"
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              type="date"
              label="Date de fin"
              name="endDate"
              control={control}
            />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputNumber
              label="Nombre d’heures prévues"
              name="hours"
              control={control}
            />
          </div>
          <div className={styles.containerField} style={{ alignItems: 'flex-end', display: 'flex' }}>
            <Checkbox
              name='archived'
              label='Phase archivée'
              control={control}
            />
          </div>
        </div>
        <hr/>
        <div className={styles.fitters}>
          <div className={styles.title}>
            <h4>Monteurs</h4>
            <button
              className={styles.add}
              disabled={isLoading || hasUnsavedChanges()}
              onClick={() => fitterModalRef.current.open()}
              title={hasUnsavedChanges() ? 'Veuillez sauvegarder la phase avant d\'ajouter un monteur' : ''}
            >
              <span className='icon'>{BiPlus({ size: 20 })}</span>
              <span>Ajouter un monteur</span>
            </button>
          </div>
          {hasUnsavedChanges() && (
            <div className={styles.warning}>
              <p>⚠️ Veuillez sauvegarder la phase avant de modifier les monteurs</p>
            </div>
          )}
          <div className={styles.list}>
            {phase.fitters.map((f:WorksiteFitter) => <Fitter
              key={f._id}
              fitter={f}
              disabled={hasUnsavedChanges()}
              openFitter={() => {
                if (!hasUnsavedChanges()) {
                  fitterModalRef?.current?.open();
                  setSelectedFitter(f);
                }
              }}
            />)}
          </div>
        </div>
        <div className={styles.buttons}>
        {phase?._id
          ? <button className={styles.delete}
            onClick={() => deletePhase()}
          >
            <span className='icon'>{IoTrash({})}</span>
            <span>Supprimer la phase</span>
          </button>
          : <span></span>}
          <button disabled={isLoading} onClick={handleSubmit(submit)}>
            <span className='icon'>{BsCheck({})}</span>
            <span>Valider</span>
          </button>
        </div>
      </div>
      <Modal ref={fitterModalRef} width={550}>
        <FitterModal
          worksite={worksite}
          phaseId={phase._id}
          fitter={selectedFitter}
          close={() => {
            fitterModalRef?.current?.close();
            setSelectedFitter(null);
          }}
        />
      </Modal>
    </>
  );
};

export default WorksitePhaseForm;
